<template>
  <div class="map_container">
    <svg class="base_map" ref="svgMap" width="100%" :height="height" >
      <g ref="world_countries"></g>
      <g ref="world_office"></g>
      <g ref="serving"></g>
      <g ref="final_destination"></g>
      <g ref="circles"></g>
      <g ref="world_lines"></g>
      <g ref="labels"></g>
    </svg>
    <div class="tooltip" ref="tooltip">
      <div class="content"></div>
    </div>
  </div>
</template>

<style lang="scss"> 
  .map_container {
    position: absolute;
    width: 100%;
    height: 100vh;

    .legend {
      top: 0;
      right: 0;

      .item {
        display: inline;
        margin: 0 5px;
      }

      span {
        display: inline-block;
        margin-right: 3px;
      }
    }

    svg {
      background: transparent;
    }

    .country-label {
      font-size: 18px;
      fill: #fff;
    }

    path.country-path {
      stroke: var(--app-color-dark);
      fill: var(--app-color-path);
      stroke-width: 0.3;
    }

    path.pandora-link {
      stroke-dasharray: 100%;
      fill: transparent;
      stroke: #000;
      stroke-width: 3;
    }

    path.country-link {
      fill: transparent;
      stroke: var(--app-color-white) !important;
      stroke-width: 2.5;
      pointer-events: none;
      opacity: 0.7;

      &.supplier-link {
        stroke: var(--app-color-pink) !important;
      }
     
      &.intermediary-link {
        stroke: var(--app-color-blue) !important;
      }
    }

    path.active-seller {
      fill: var(--app-color-pink) !important;
      stroke: var(--app-color-black) !important;
    }

    path.active-intermediary {
      fill: var(--app-color-blue) !important;
      stroke: var(--app-color-black) !important;
    }

    path.active-buyer {
      fill: var(--app-color-green) !important;
      stroke: var(--app-color-black) !important;
    }

    path.active-residency {
      fill: var(--app-color-green) !important;
      stroke: var(--app-color-black) !important;
    }

    circle {
      stroke: #fff !important;
      stroke-width: 1px;
      fill: rgba(255, 255, 255, 0.3) !important;
      cursor: pointer;

      &:hover {
        fill: rgba(0, 0, 0, 0.3) !important;
      }
    }

    svg.base_map {
      opacity: 1;
      transition: opacity 1.5s ease-in-out;
    }
  }

  div.tooltip {	
    position: absolute;			
    border: 0px;
    pointer-events: none;
    opacity: 0;

    .content {
      color: var(--app-color-black);
      text-align: center;								
      padding: 5px;				
      font: 16px sans-serif;
      background: var(--app-color-light);	
    }
  }

</style>

<script>
  import * as d3 from 'd3'
  import { geoCylindricalStereographic } from 'd3-geo-projection'
  import utils from "@/mixins/utils";

  export default {
    name: 'HeaderMap',
    mixins: [utils],
    props: {
      servingIn: [Array, String],
      appointedBy: [Array, String],
      finalDestination: [Array, String],
      initialScale: [Number, String],
      initialCenter: [String, Array],
    },
    mounted() {
      setTimeout(() => {
        this.renderMap()

        window.addEventListener("resize", this.resizeMap);
      }, 100)
    },
    data() {
      return {
        openMenu: false,
        center: [0,0],
        scale: 400,
        distance: 0,
        bounds: [],
        zoomed: false,
        tooltip: ``,
        animationSpeed: 350,
        currentCenter: [],
        currentScale: 0,
        width: 720
      }
    },
    watch: {
      optionsMap(v) {
        this.animateMap(v)
      },
      finalDestination() {
        this.renderMap()
      },
      currentCenter() {
        document.querySelector('.narrative-container').classList.remove(`active`)
        document.querySelector('.source-div').classList.remove(`active`)
      },
      width() {
        this.animateMap(this.optionsMap)
      }
    },
    computed: {
      height() {
        return window.innerHeight
      },
      optionsMap() {
        return {
          scale: this.initialScale,
          center: this.initialCenter
        }
      },
			world_json() {
				return require("@/data/worldmap.json")
			},
			countries() {
				return require("@/data/countries.json")
			},
      projection () {
        return geoCylindricalStereographic()
          .translate([this.width/2, this.height/1.5])
          .scale(this.scale)
      },
			path () {
        return d3.geoPath().projection(this.projection)
      },
      appointedByParse() {
        if(this.appointedBy)
          return this.appointedBy.split(";")
        return []
      },
      servingInParse() {
        if(this.servingIn)
          return this.servingIn.split(";")
        return []
      },
      finalInParse() {
        if(this.finalDestination)
          return this.finalDestination.split(";")
        return []
      },
      circlesPoints() {
        return [
          this.countries[this.appointedBy],
          this.countries[this.servingIn],
          this.countries[this.finalDestination]
        ]
      },
      labelsList() {
        return [
          {
            name: this.translateIsoCountry(this.appointedBy),
            center: this.countries[this.appointedBy]
          },
          {
            name: this.translateIsoCountry(this.servingIn),
            center: this.countries[this.servingIn]
          },
          {
            name: this.translateIsoCountry(this.finalDestination),
            center: this.countries[this.finalDestination]
          }          
        ]
        
      },
      links() {
        return [{
          type: "LineString",
          from: this.appointedBy,
          to: this.servingIn,
          type_link: 'supplier-link',
          coordinates: [this.countries[this.appointedBy],this.countries[this.servingIn]]
        },
        {
          type: "LineString",
          from: this.servingIn,
          to: this.servingIn,
          type_link: 'intermediary-link',
          coordinates: [this.countries[this.servingIn],this.countries[this.finalDestination]]
        }]
      },
      center_device() {
        return [this.width/2.2, this.height/2.7]
      }
    },
    methods: {
      resizeMap() {
        this.renderMap()
      },
      renderLabels(){
        let labels = d3.select(this.$refs['labels'])        
        labels
          .selectAll('text')
          .data(this.labelsList)
          .enter()
          .append('text')
          .attr("x", country => {
            if(country.name == 'New Zealand')
              return this.projection(country.center)[0] - 140
            return this.projection(country.center)[0] + 10
          })
          .attr("y", country => {
            if(country.name == 'New Zealand')
              return this.projection(country.center)[1] + 15
            if(country.name == 'Italy')
              return this.projection(country.center)[1] - 10
            if(country.name == 'Tailand')
              return this.projection(country.center)[1] + 15
            return this.projection(country.center)[1]
          })
          .attr('class', 'country-label')
          .attr('pointer-events', 'none')
          .text(country => `${country.name}`)
      },
      renderMap() {
        this.width = Math.max(
          document.documentElement["clientWidth"],
          document.body["scrollWidth"],
          document.documentElement["scrollWidth"],
          document.body["offsetWidth"],
          document.documentElement["offsetWidth"]
        )
      
        this.tooltip = d3.select(this.$refs['tooltip'])
				this.bounds = d3.geoBounds(this.world_json)
				this.distance = d3.geoDistance(this.bounds[0], this.bounds[1])
        let base = d3.select(this.$refs['world_countries'])

        this.scale = this.initialScale

				this.projection
          .scale(this.scale)
          .center(this.initialCenter)
          .translate(this.center_device)

        base.selectAll('path')
          .data(this.world_json.features)
          .enter()
          .append('path')
          .attr('d', this.path)
          .attr('id', d => {
            return `${d.properties.ID}`
          })
        
        this.renderCountries()
        this.animateMap()
      },
      renderCircles () {
        let base = d3.select(this.$refs['circles'])
        base.selectAll('circle.country-point').remove()
        base
          .selectAll('circle')
          .data(this.circlesPoints)
          .enter()
          .append('circle')
          .attr("cx", country => this.projection(country)[0])
          .attr("cy", country => this.projection(country)[1])
          .attr("r", 3)
          .attr("class", `country-point`)
      },
      renderCountries() {
        let base = d3.select(this.$refs['world_countries'])
        base.selectAll('path')
          .attr('class', d => {

            if(this.appointedByParse.includes(d.properties.ID)) {
              return `active-seller country-path path-${d.properties.ID}`
            }
            
            if(this.servingInParse.includes(d.properties.ID)) {
              return `active-intermediary country-path path-${d.properties.ID}`
            }

            if(this.finalInParse.includes(d.properties.ID)) {
              return `active-buyer country-path path-${d.properties.ID}`
            }

            return `country-path path-${d.properties.ID}`
          })
      },
      renderLink() {
        let base = d3.select(this.$refs['world_lines'])
        base.selectAll('path.country-link').remove()
        base.selectAll('path')
          .data(this.links)
          .enter()
          .append('path')
          .attr('class', item => {
            return `country-link ${item.type_link}`
          })
          .attr('d', this.path)
      },
      startMap() {
        let svgMap = d3.select(this.$refs['svgMap'])
        svgMap.selectAll('text').remove()
        svgMap.selectAll('circle').remove()
        svgMap.selectAll('path.country-link').remove()
      },
      endMap() {
        this.renderLink()
        this.renderCircles()
        this.renderLabels()

        this.currentCenter = this.initialCenter
        this.currentScale = this.initialScale
      },
      animateMap() {
        let svgMap = d3.select(this.$refs['svgMap'])
        d3.selectAll('path').classed('active', false)

        svgMap.selectAll('path')
          .transition()
          .duration(this.animationSpeed)
          .attrTween('d', (d) => {
            const r = d3.interpolate(this.currentCenter, this.optionsMap.center)
            const s = d3.interpolate(this.currentScale, this.optionsMap.scale)

            return (t) => {
              this.projection
                .scale(this.currentScale < this.optionsMap.scale ? s(Math.pow(t,1)) : s(Math.pow(t,2)))
                .center(r(Math.pow(t,0.33)))

              this.path.projection(this.projection)

              return this.path(d)
            }
          })
          .on('start', this.startMap)
          .on('end', this.endMap)

      //   $this.mapSVG.select('g.labels').style('opacity', 0)

      //     $this.mapSVG.selectAll('text')
      //     .transition()
      //     .duration($this.animationSpeed)
      //     .attrTween('transform', function(country) {
      //       return function() {
      //         return 'translate(' + $this.path.centroid(country) + ')'
      //       }
      //     })
      //     .on('end', function() {
      //       if($this.scale > 560)
      //         $this.mapSVG.select('g.labels').style('opacity', 1)
      //     })

        svgMap.selectAll('circle')
          .transition()
          .duration(this.animationSpeed)
          .attrTween('transform', (country) => {
            return () => {
              return `translate(${this.projection(country)[0]}, ${this.projection(country)[1]})`
            }
          })
      }
    }
  }
</script>